import supabase from '../supabase/supabase';
import { reportService } from './reportService';

export const AP_AGING_BUCKETS = ['current', '1-30', '31-60', '61-90', '91+'];
export const AP_AGING_BUCKET_LABELS = {
  current: 'Current',
  '1-30': '1–30 Days',
  '31-60': '31–60 Days',
  '61-90': '61–90 Days',
  '91+': '91+ Days'
};

const INCLUDED_STATUSES = new Set(['unpaid', 'partially paid', 'overdue']);

const createBuckets = () => ({
  current: 0,
  '1-30': 0,
  '31-60': 0,
  '61-90': 0,
  '91+': 0
});

const money = (value) => Number(Number(value || 0).toFixed(2));

const normalizeStatus = (status) => String(status || '')
  .trim()
  .toLowerCase()
  .replace(/[-_]+/g, ' ')
  .replace(/\s+/g, ' ');

const getBucket = (dueDate, asOfDate) => {
  if (!dueDate) return 'current';

  const due = new Date(`${dueDate}T00:00:00`);
  const asOf = new Date(`${asOfDate}T00:00:00`);
  const agingDays = Math.floor((asOf - due) / 86400000);

  if (agingDays <= 0) return 'current';
  if (agingDays <= 30) return '1-30';
  if (agingDays <= 60) return '31-60';
  if (agingDays <= 90) return '61-90';
  return '91+';
};

const createVendor = (id, name) => ({
  id,
  name: name || 'Unassigned vendor',
  bills: [],
  buckets: createBuckets(),
  total: 0
});

const getEmptyReason = (diagnostics) => {
  if (diagnostics.billsFound === 0) return 'No bills found.';
  if (diagnostics.includedBills === 0 && diagnostics.positiveBalanceBills === 0) {
    return 'All bills are paid.';
  }
  if (diagnostics.includedBills === 0) return 'No open bills found.';
  return 'No outstanding payables found for the selected date.';
};

export const apAgingService = {
  async generate(asOfDate, accountId, businessName = 'Your Business') {
    const { account_id } = await reportService.getBaseData(accountId);

    const [{ data: bills, error: billsError }, { data: vendors, error: vendorsError }] = await Promise.all([
      supabase
        .schema('accounting')
        .from('bills_20240522')
        .select('id, vendor_id, bill_number, issue_date, due_date, amount, status')
        .eq('account_id', account_id)
        .lte('issue_date', asOfDate)
        .order('due_date', { ascending: true }),
      supabase
        .schema('accounting')
        .from('vendors_20240522')
        .select('id, name')
        .eq('account_id', account_id)
    ]);

    if (billsError) throw billsError;
    if (vendorsError) throw vendorsError;

    const vendorNames = new Map(
      (vendors || []).map((vendor) => [vendor.id, vendor.name])
    );
    const vendorsById = new Map();
    let includedBills = 0;
    let positiveBalanceBills = 0;

    (bills || []).forEach((bill) => {
      const amount = money(bill.amount);
      const status = normalizeStatus(bill.status);

      if (amount > 0) positiveBalanceBills += 1;
      if (!INCLUDED_STATUSES.has(status) || amount <= 0) return;

      includedBills += 1;
      const vendorId = bill.vendor_id || `unassigned-${bill.id}`;
      const vendor = vendorsById.get(vendorId) || createVendor(
        vendorId,
        vendorNames.get(vendorId)
      );
      const dueDate = bill.due_date || bill.issue_date;
      const bucket = getBucket(dueDate, asOfDate);

      vendor.bills.push({
        id: bill.id,
        number: bill.bill_number || '—',
        issueDate: bill.issue_date,
        dueDate,
        amount,
        bucket,
        status: bill.status || 'Unpaid'
      });
      vendor.buckets[bucket] = money(vendor.buckets[bucket] + amount);
      vendor.total = money(vendor.total + amount);
      vendorsById.set(vendorId, vendor);
    });

    const vendorRows = Array.from(vendorsById.values())
      .map((vendor) => ({
        ...vendor,
        bills: vendor.bills.sort((a, b) => (
          String(a.dueDate || '').localeCompare(String(b.dueDate || ''))
        ))
      }))
      .sort((a, b) => b.total - a.total);

    const grandTotals = {
      buckets: createBuckets(),
      total: 0
    };

    vendorRows.forEach((vendor) => {
      AP_AGING_BUCKETS.forEach((bucket) => {
        grandTotals.buckets[bucket] = money(
          grandTotals.buckets[bucket] + vendor.buckets[bucket]
        );
      });
      grandTotals.total = money(grandTotals.total + vendor.total);
    });

    const diagnostics = {
      vendorsFound: (vendors || []).length,
      billsFound: (bills || []).length,
      includedBills,
      excludedBills: Math.max(0, (bills || []).length - includedBills),
      positiveBalanceBills,
      totalOutstandingBalance: grandTotals.total
    };

    console.info('[AP Aging] Database verification', diagnostics);

    return {
      businessName,
      vendors: vendorRows,
      grandTotals,
      diagnostics,
      emptyReason: vendorRows.length ? '' : getEmptyReason(diagnostics)
    };
  }
};